import { ResponseType } from "~/api/response-types/types";
import axios, { AxiosRequestConfig, Method } from "axios";
import { isCallable } from "~/utils/function-utils";

export interface ServerResponse<TResponse> {
    ok: boolean;
    redirected: boolean;
    status: number;
    statusText: string;
    type: string;
    url: string;
    data: TResponse;
}

export interface ICallServerParams<T = object> {
    endpoint: string;
    requestType: Method;
    body?: T;
}

export function padRootUrl(endpoint: string) {
    return `${window.location.protocol}//${window.location.hostname}${endpoint}`;
}

export function callServer<TResponse>(
    endpoint: ICallServerParams["endpoint"],
    requestType: ICallServerParams["requestType"],
    body?: ICallServerParams["body"]
) {
    return new Promise<ServerResponse<TResponse>>(async (resolve, reject) => {
        const init: AxiosRequestConfig = {
            method: requestType,
            headers: {}
        };

        if (init.method && init.method.toLowerCase() === "post") {
            init.headers = {
                ...init.headers,
                Accept: "application/json",
                "Content-Type": "application/json"
            };

            init.data = JSON.stringify(body);
        }

        if (process.env.API_DUMMY_DATA && window.parent.DUMMY_RESPONSE) {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const { dummyDataResolver } = require("~/api/dummy-data-resolver");
            // window.parent represents the outer storybook window object into which the dummy response is injected
            const responseType = window.parent.DUMMY_RESPONSE;
            console.log("callServer called with responseType: ", responseType);
            const resType = responseType !== undefined ? responseType : ResponseType.success;
            const dummyData = dummyDataResolver(endpoint, requestType, resType as ResponseType, body);
            const dummyDelay: number = process.env.API_DUMMY_DELAY
                ? parseInt(process.env.API_DUMMY_DELAY, 10)
                : 0;

            if (dummyData) {
                const data = isCallable(dummyData.data)
                    ? dummyData.data({
                          endpoint,
                          requestType,
                          body
                      })
                    : dummyData.data;

                window.setTimeout(() => {
                    resType === ResponseType.success ? resolve(data) : reject(data);
                }, dummyDelay);
                return;
            } else {
                throw new Error("Please provide api dummy data for server call");
            }
        }

        executeCall<TResponse>(endpoint, init, resolve, reject);
    });
}

function handleErrors(response: any) {
    const { data } = response;
    if (!data.success) {
        throw data;
    }
    return data;
}

function executeCall<TResponse>(
    url: string,
    r: AxiosRequestConfig,
    resolve: (value?: ServerResponse<TResponse>) => void,
    reject: (reason?: Error) => void
) {
    axios({ url, ...r })
        .then(handleErrors)
        .then(response => {
            resolve(response);
        })
        .catch((error: Error) => {
            reject(error);
        });
}
